import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@mui/material';


import { useTranslate } from 'src/locales';
import {
  useCreateAvailabilityRuleMutation,
  useUpdateAvailabilityRuleByIdMutation
} from 'src/services/calendar/calendar.service';
import {
  AvailabilityRule,
  AvailabilityRuleType,
  AvailabilityType,
  CreateAvailabilityRuleDto,
  DayOfWeek,
  RollingWindow,
  UpdateAvailabilityRuleDto
} from 'src/services/calendar/calendar.types';

import { Box } from '@mui/system';
import _ from 'lodash';
import FeatureLockedListView from 'src/components/billing/feature-locked-list-view';
import FormProvider, {
  RHFAutocomplete,
  RHFDatePicker,
  RHFSelect,
  RHFTimePicker
} from 'src/components/hook-form';
import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';
import { FeatureType } from 'src/types/subscription.types';

type AvailabilityRuleEditorModalProps = {
  open: boolean;
  onClose: () => void;
  selectedRule: AvailabilityRule | null;
  eventTypeId: string;
};

export default function AvailabilityRuleEditorModal({
  open,
  onClose,
  selectedRule,
  eventTypeId,
}: AvailabilityRuleEditorModalProps) {

  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { canAccess } = useFeatures();

  const [createRule, { isLoading: isCreating }] = useCreateAvailabilityRuleMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateAvailabilityRuleByIdMutation();

  // Form validation schema
  const AvailabilityRuleSchema = Yup.object().shape({
    rule_type: Yup.string().required(t('validation.required')),
    type: Yup.string().required(t('validation.required')),
    start_time: Yup.string().required(t('validation.required')),
    end_time: Yup.string().required(t('validation.required')),
    date: Yup.mixed().when('rule_type', {
      is: AvailabilityRuleType.SPECIFIC_DATE,
      then: () => Yup.date().required(t('validation.required')),
      otherwise: () => Yup.mixed().nullable(),
    }),
    start_date: Yup.mixed().when('rule_type', {
      is: AvailabilityRuleType.DATE_RANGE,
      then: () => Yup.date().required(t('validation.required')),
      otherwise: () => Yup.mixed().nullable(),
    }),
    end_date: Yup.mixed().when('rule_type', {
      is: AvailabilityRuleType.DATE_RANGE,
      then: () => Yup.date().required(t('validation.required')),
      otherwise: () => Yup.mixed().nullable(),
    }),
    days_of_week: Yup.mixed().when('rule_type', {
      is: AvailabilityRuleType.WEEKLY_RECURRING,
      then: () => Yup.array().of(Yup.mixed().oneOf(Object.values(DayOfWeek), t('validation.invalid'))).required(t('validation.required')),
      otherwise: () => Yup.mixed().nullable(),
    }),
    days_of_month: Yup.mixed().when('rule_type', {
      is: AvailabilityRuleType.MONTHLY_RECURRING,
      then: () =>
        Yup.array()
          .of(Yup.number().min(1, t('validation.min', { min: 1 })).max(31, t('validation.max', { max: 31 })))
          .required(t('validation.required')),
      otherwise: () => Yup.mixed().nullable(),
    }),
    priority: Yup.number()
      .required(t('validation.required'))
      .min(0, t('validation.min', { min: 0 }))
      .max(2, t('validation.max', { max: 2 })),
    rolling_window: Yup.string().oneOf(Object.values(RollingWindow)).nullable(),
  });

  // Define the form value types
  type FormValues = {
    rule_type: AvailabilityRuleType;
    type: AvailabilityType;
    date: Date | null;
    start_date: Date | null;
    end_date: Date | null;
    start_time: string;
    end_time: string;
    days_of_week: DayOfWeek[];
    days_of_month: number[];
    priority: number;
    rolling_window?: RollingWindow | null;
  };

  const defaultValues: FormValues = useMemo(() => ({
    rule_type: AvailabilityRuleType.SPECIFIC_DATE,
    type: AvailabilityType.AVAILABLE,
    date: null,
    start_date: null,
    end_date: null,
    start_time: '',
    end_time: '',
    days_of_week: [],
    days_of_month: [],
    priority: 0,
    rolling_window: null,
  }), []);

  const methods = useForm<FormValues>({
    resolver: yupResolver(AvailabilityRuleSchema) as any,
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;

  const values = watch();

  const editMode = useMemo(() => !!selectedRule, [selectedRule]);

  const handleCloseDialog = () => {
    reset(defaultValues);
    onClose();
  }

  useEffect(() => {
    if (selectedRule) {
      reset({
        rule_type: selectedRule.rule_type as AvailabilityRuleType,
        type: selectedRule.type as AvailabilityType,
        date: selectedRule.date ? new Date(selectedRule.date) : null,
        start_date: selectedRule.start_date ? new Date(selectedRule.start_date) : null,
        end_date: selectedRule.end_date ? new Date(selectedRule.end_date) : null,
        start_time: selectedRule.start_time || '',
        end_time: selectedRule.end_time || '',
        days_of_week: Object.keys(selectedRule.days_of_week || {}).map(Number) as DayOfWeek[],
        days_of_month: Object.keys(selectedRule.days_of_month || {}).map(Number),
        priority: selectedRule.priority !== undefined ? selectedRule.priority : 0,
        rolling_window: selectedRule.rolling_window
      });
    } else {
      reset(defaultValues);
    }
  }, [selectedRule, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data: FormValues) => {
    try {
      if (selectedRule) {
        // Update existing rule
        const updateData: UpdateAvailabilityRuleDto = {
          date: data.date || undefined,
          start_date: data.start_date || undefined,
          end_date: data.end_date || undefined,
          start_time: data.start_time,
          end_time: data.end_time,
          days_of_week: data.days_of_week || undefined,
          days_of_month: data.days_of_month || undefined,
          priority: data.priority,
          rolling_window: data.rolling_window || undefined,
        };

        await updateRule({
          eventTypeId,
          ruleId: selectedRule.id,
          data: updateData,
        }).unwrap();

        enqueueSnackbar(t('account.event_type_availability.api.update.success'), { variant: 'success' });
      } else {
        // Create new rule
        const createData: CreateAvailabilityRuleDto = {
          rule_type: data.rule_type,
          type: data.type,
          date: data.date || undefined,
          start_date: data.start_date || undefined,
          end_date: data.end_date || undefined,
          start_time: data.start_time,
          end_time: data.end_time,
          days_of_week: data.days_of_week || undefined,
          days_of_month: data.days_of_month || undefined,
          priority: data.priority,
          rolling_window: data.rolling_window || undefined,
        };

        await createRule({
          eventTypeId,
          data: createData,
        }).unwrap();

        enqueueSnackbar(t('account.event_type_availability.api.create.success'), { variant: 'success' });
      }

      handleCloseDialog();
    } catch (error) {
      enqueueSnackbar(
        selectedRule
          ? t('account.event_type_availability.api.update.default_error')
          : t('account.event_type_availability.api.create.default_error'),
        { variant: 'error' }
      );
    }
  });

  const renderRuleTypeFields = () => {
    const ruleType = values.rule_type;

    switch (ruleType) {
      case AvailabilityRuleType.SPECIFIC_DATE:
        return (
          <Grid item xs={12}>
            <RHFDatePicker
              name="date"
              label={t('account.availability_settings.date')}
            />
          </Grid>
        );
      case AvailabilityRuleType.DATE_RANGE:
        return (
          <>
            <Grid item xs={12} md={6}>
              <RHFDatePicker
                name="start_date"
                label={t('account.availability_settings.start_date')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFDatePicker
                name="end_date"
                label={t('account.availability_settings.end_date')}
              />
            </Grid>
          </>
        );
      case AvailabilityRuleType.WEEKLY_RECURRING:
        return (
          <Grid item xs={12}>
            <RHFAutocomplete
              multiple
              fullWidth
              disableCloseOnSelect
              name="day_of_week"
              label={t('account.availability_settings.days_of_week')}
              options={Object.values(DayOfWeek).filter((day) => typeof day === 'number')}
              value={values.days_of_week}
              filterOptions={(x) => x}
              onChange={(event, newValue) => {
                setValue('days_of_week', newValue as DayOfWeek[]);
              }}
              getOptionLabel={(option) => t(`common.days.${_.lowerCase(DayOfWeek[option as number])}`)}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionKey={(option) => option.toString()}
            />
          </Grid>
        );
      case AvailabilityRuleType.MONTHLY_RECURRING:
        return (
          <Grid item xs={12}>
            <RHFAutocomplete
              multiple
              fullWidth
              disableCloseOnSelect
              name="days_of_month"
              label={t('account.availability_settings.days_of_month')}
              options={Array.from({ length: 31 }, (val, i) => i + 1)} // Options from 1 to 31
              value={values.days_of_month}
              filterOptions={(x) => x}
              onChange={(event, newValue) => {
                setValue('days_of_month', newValue as number[]);
              }}
              getOptionLabel={(option) => option.toString()}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionKey={(option) => option.toString()}
            />
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>

      <DialogTitle>
        {selectedRule
          ? t('account.event_type_availability.edit_rule')
          : t('account.event_type_availability.add_rule')}
      </DialogTitle>

      {
        canAccess(FeatureType.EVENT_SCHEDULING_ENABLED) ? (
          <FormProvider methods={methods} onSubmit={onSubmit}>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <RHFSelect name="rule_type" label={t('account.availability_settings.rule_type')} disabled={editMode}>
                    <MenuItem value={AvailabilityRuleType.SPECIFIC_DATE}>
                      {t('account.availability_settings.specific_date')}
                    </MenuItem>
                    <MenuItem value={AvailabilityRuleType.DATE_RANGE}>
                      {t('account.availability_settings.date_range')}
                    </MenuItem>
                    <MenuItem value={AvailabilityRuleType.DAILY_RECURRING}>
                      {t('account.availability_settings.daily_recurring')}
                    </MenuItem>
                    <MenuItem value={AvailabilityRuleType.WEEKLY_RECURRING}>
                      {t('account.availability_settings.weekly_recurring')}
                    </MenuItem>
                    <MenuItem value={AvailabilityRuleType.MONTHLY_RECURRING}>
                      {t('account.availability_settings.monthly_recurring')}
                    </MenuItem>
                  </RHFSelect>
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFSelect name="type" label={t('account.availability_settings.availability_type')}>
                    <MenuItem value={AvailabilityType.AVAILABLE}>
                      {t('account.availability_settings.available')}
                    </MenuItem>
                    <MenuItem value={AvailabilityType.UNAVAILABLE}>
                      {t('account.availability_settings.unavailable')}
                    </MenuItem>
                  </RHFSelect>
                </Grid>

                {renderRuleTypeFields()}

                <Grid item xs={12} md={6}>
                  <RHFTimePicker
                    name="start_time"
                    label={t('account.availability_settings.start_time')}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTimePicker
                    name="end_time"
                    label={t('account.availability_settings.end_time')}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RHFSelect name="priority" label={t('account.availability_settings.priority.label')}>
                    <MenuItem value={0}>
                      {t('account.availability_settings.priority.standard')}
                    </MenuItem>
                    <MenuItem value={1}>
                      {t('account.availability_settings.priority.high')}
                    </MenuItem>
                    <MenuItem value={2}>
                      {t('account.availability_settings.priority.highest')}
                    </MenuItem>
                  </RHFSelect>
                </Grid>

                {
                  [
                    AvailabilityRuleType.DAILY_RECURRING,
                    AvailabilityRuleType.WEEKLY_RECURRING,
                    AvailabilityRuleType.MONTHLY_RECURRING
                  ].includes(values.rule_type) && (
                    <Grid item xs={12}>
                      <RHFSelect name="rolling_window"
                        label={t('account.availability_settings.rolling_window.label')}
                        helperText={t('account.availability_settings.rolling_window.helper')}
                      >
                        <MenuItem value={RollingWindow.ONGOING}>
                          {t('account.availability_settings.rolling_window.options.ongoing')}
                        </MenuItem>
                        <MenuItem value={RollingWindow.ONE_WEEK}>
                          {t('account.availability_settings.rolling_window.options.one_week')}
                        </MenuItem>
                        <MenuItem value={RollingWindow.TWO_WEEKS}>
                          {t('account.availability_settings.rolling_window.options.two_weeks')}
                        </MenuItem>
                        <MenuItem value={RollingWindow.THREE_WEEKS}>
                          {t('account.availability_settings.rolling_window.options.three_weeks')}
                        </MenuItem>
                        <MenuItem value={RollingWindow.ONE_MONTH}>
                          {t('account.availability_settings.rolling_window.options.one_month')}
                        </MenuItem>
                        <MenuItem value={RollingWindow.THREE_MONTHS}>
                          {t('account.availability_settings.rolling_window.options.three_months')}
                        </MenuItem>
                        <MenuItem value={RollingWindow.SIX_MONTHS}>
                          {t('account.availability_settings.rolling_window.options.six_months')}
                        </MenuItem>
                        <MenuItem value={RollingWindow.ONE_YEAR}>
                          {t('account.availability_settings.rolling_window.options.one_year')}
                        </MenuItem>
                      </RHFSelect>
                    </Grid>
                  )
                }

              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isCreating || isUpdating}
              >
                {selectedRule ? t('common.update') : t('common.create')}
              </LoadingButton>
            </DialogActions>
          </FormProvider>
        ) : (
          <Box sx={{ p: 2, pb: 8 }}>
            <FeatureLockedListView text={t('billing.subscription_required')} onNavigate={onClose} />
          </Box>
        )
      }
    </Dialog>
  );
}